<script setup lang="ts">
/**
 * AppLayout - ChatGPT 风格布局: 左侧栏 + 右侧主区.
 *
 * 桌面 (lg+): 侧栏常驻 (~260px, static 块), 主区填满剩余宽度.
 * 移动 (<lg): 侧栏隐藏; 主区顶部栏汉堡按钮 -> 抽屉滑入 + 背景遮罩;
 *   点遮罩 / 切路由 / 点侧栏内项 -> 收起抽屉.
 * 底部 tab bar 已移除 (由侧栏承担全部导航).
 *
 * 抽屉状态集中在此 (sidebarOpen), 通过 provideSidebarDrawer 暴露给
 * 子路由 (Conversation 页头部汉堡需触发打开).
 *
 * 主区结构: 移动顶栏 (lg:hidden, 非对话路由) + 视图滚动容器.
 *   对话路由自带顶栏汉堡, 故 AppLayout 顶栏在 conversation 路由下隐藏避免重复.
 */
import { computed, ref, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { Bars3Icon } from '@heroicons/vue/24/outline'
import AppSidebar from './AppSidebar.vue'
import { provideSidebarDrawer } from '@/composables/useSidebarDrawer'

const route = useRoute()
const sidebarOpen = ref(false)

function openSidebar(): void {
  sidebarOpen.value = true
}
function closeSidebar(): void {
  sidebarOpen.value = false
}

provideSidebarDrawer({
  open: sidebarOpen,
  openSidebar,
  closeSidebar,
})

// 路由切换 -> 收起抽屉 (移动端)
watch(
  () => route.path,
  () => {
    sidebarOpen.value = false
  },
)

// 对话页自带顶栏汉堡, AppLayout 的移动顶栏在该路由下隐藏, 避免重复.
const isConversationRoute = computed<boolean>(() => route.name === 'conversation')
const headerTitle = computed<string>(
  () => (route.meta.title as string | undefined) ?? '知行AI',
)
</script>

<template>
  <div class="flex h-[100dvh] bg-white">
    <!-- 侧栏 (桌面常驻 / 移动抽屉) -->
    <AppSidebar :open="sidebarOpen" @close="closeSidebar" />

    <!-- 移动抽屉背景遮罩 -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 z-30 bg-black/40 lg:hidden"
      role="button"
      aria-label="关闭侧栏"
      tabindex="-1"
      @click="closeSidebar"
    ></div>

    <!-- 主区 -->
    <main class="flex min-w-0 flex-1 flex-col">
      <!-- 移动顶栏 (非对话路由: 对话页自带头部) -->
      <header
        v-if="!isConversationRoute"
        class="flex shrink-0 items-center gap-2 border-b border-gray-200/70 bg-white px-2 py-2.5 pt-safe lg:hidden"
      >
        <button
          type="button"
          class="rounded-full p-2 text-gray-500 active:bg-gray-100"
          aria-label="打开侧栏"
          @click="openSidebar"
        >
          <Bars3Icon class="h-5 w-5" :aria-hidden="true" />
        </button>
        <span class="text-base font-medium text-gray-800">{{ headerTitle }}</span>
      </header>

      <div class="relative flex-1 overflow-hidden">
        <RouterView v-slot="{ Component: comp }">
          <component :is="comp" :key="route.path" />
        </RouterView>
      </div>
    </main>
  </div>
</template>
